import { Link } from 'react-router-dom'
import { Heart, MapPin, ShieldCheck } from 'lucide-react'
import MarketingHeader from '../components/MarketingHeader'
import MarketingFooter from '../components/MarketingFooter'
import { useSeo } from '../lib/seo'

// ── Founders ──────────────────────────────────────────────────────────
// Drop real photos into /public/founders/ as founder-1.jpg / founder-2.jpg.
// Until they exist, each card shows an initials placeholder automatically —
// that's the reserved spot where the photo will appear.
const FOUNDERS = [
  { name: 'Temitope Aladegbaye', role: 'Co-founder · COO & CFO', img: '/founders/founder-1.jpg' },
  { name: 'Ayomide Aladegbaye',  role: 'Co-founder · CEO & CTO', img: '/founders/founder-2.jpg' },
]

const VALUES = [
  { icon: MapPin,      title: 'Built in Nigeria',        desc: 'Made for our events, our traditions, and the way our weddings actually run — not a foreign app with a wedding skin.' },
  { icon: Heart,       title: 'Couples first',           desc: 'Every feature starts with one question: does this give the couple back time, calm, and joy?' },
  { icon: ShieldCheck, title: 'Every naira respected',   desc: 'Cash gifts and aso ebi settle straight to your own account. We hold your trust as carefully as your money.' },
]

function FounderCard({ f }) {
  const initials = f.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  return (
    <div className="about-founder" style={{ background: 'white', border: '1px solid #E5E0D8', borderRadius: '1.5rem', overflow: 'hidden' }}>
      <div style={{ aspectRatio: '1 / 1', position: 'relative', background: 'linear-gradient(135deg,#F3EFE9,#ECE3D8)', overflow: 'hidden' }}>
        {/* Reserved photo spot — initials placeholder shows until a real photo is dropped in */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.7rem' }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(196,130,106,0.14)', border: '1px solid rgba(196,130,106,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Gibs, serif', fontSize: '1.6rem', color: '#C4826A' }}>{initials}</div>
          <span style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', fontWeight: 600, color: '#B6A695', letterSpacing: '0.05em' }}>Photo coming soon</span>
        </div>
        {/* Real photo — covers the placeholder automatically once /public/founders/ has it */}
        <img
          src={f.img}
          alt={f.name}
          onError={e => { e.currentTarget.style.display = 'none' }}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
      <div style={{ padding: '1.25rem 1.5rem', textAlign: 'center' }}>
        <h3 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.35rem', color: '#1A1A1A', margin: '0 0 0.2rem' }}>{f.name}</h3>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#C4826A', letterSpacing: '0.08em', textTransform: 'uppercase', margin: 0 }}>{f.role}</p>
      </div>
    </div>
  )
}

export default function AboutPage() {
  useSeo({ title: 'Our Story', description: 'Why we built Okoiya — one beautiful place for Nigerian couples to plan every part of their wedding, from the first invite to the last dance.', path: '/about' })
  return (
    <div style={{ background: '#FAFAF8', minHeight: '100vh' }}>
      <MarketingHeader/>

      {/* Hero */}
      <section className="about-hero" style={{ textAlign: 'center', padding: '5rem 2rem 3.5rem', maxWidth: 760, margin: '0 auto' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(155,175,155,0.15)', border: '1px solid rgba(155,175,155,0.3)', borderRadius: 100, padding: '0.35rem 1rem', marginBottom: '1.5rem' }}>
          <span style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.72rem', fontWeight: 700, color: '#7A917A', letterSpacing: '0.06em' }}>OUR STORY</span>
        </div>
        <h1 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: 'clamp(2.2rem,5vw,3.4rem)', color: '#1A1A1A', margin: '0 0 1.25rem', lineHeight: 1.15 }}>
          Built for the way Nigerian weddings really happen
        </h1>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '1.05rem', color: '#6B6B6B', lineHeight: 1.8, margin: 0 }}>
          Okoiya started with a simple frustration: planning a wedding meant juggling endless WhatsApp groups, spreadsheets, and apps never built for our traditions. We set out to give couples one beautiful place for every event, every guest, and every moment.
        </p>
      </section>

      {/* Our Story — image + shared narrative (asymmetric two-column) */}
      <section className="about-story" style={{ maxWidth: 1040, margin: '0 auto', padding: '2rem 2rem 3rem', display: 'grid', gridTemplateColumns: '0.9fr 1.1fr', gap: '3rem', alignItems: 'center' }}>
        <div className="about-story-img" style={{ borderRadius: '1.5rem', overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.12)', aspectRatio: '4 / 5' }}>
          <img
            src="/brand/regal-red.webp"
            srcSet="/brand/regal-red-700.webp 700w, /brand/regal-red.webp 1100w"
            sizes="(max-width:860px) 90vw, 460px"
            alt="A couple celebrating their traditional wedding"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </div>
        <div className="about-story-text">
          <h2 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: 'clamp(1.7rem,3.2vw,2.4rem)', color: '#1A1A1A', margin: '0 0 1.25rem', lineHeight: 1.2 }}>
            How Okoiya began
          </h2>
          {/* ↓ This is your shared story — edit it into your own words/specifics any time. */}
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '1rem', color: '#4A4A4A', lineHeight: 1.85, margin: '0 0 1.1rem' }}>
            Okoiya began the way most good things do — out of frustration. We watched people we love plan their weddings across a dozen WhatsApp groups, half-finished spreadsheets, and apps that were never built with our traditions in mind. The introduction, the traditional, the white wedding, the aso ebi, the hundreds of guests — all held together by sheer willpower and a group chat.
          </p>
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '1rem', color: '#4A4A4A', lineHeight: 1.85, margin: '0 0 1.1rem' }}>
            We knew it could feel different. So we built Okoiya: one beautiful place where a Nigerian couple can hold every event, every guest, and every naira — and actually enjoy the season leading up to their day.
          </p>
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '1rem', color: '#4A4A4A', lineHeight: 1.85, margin: 0 }}>
            We&apos;re <strong style={{ color: '#1A1A1A' }}>Temitope &amp; Ayomide</strong> — and we&apos;re building Okoiya so that planning a wedding feels less like a second job, and more like the celebration it was always meant to be.
          </p>
        </div>
      </section>

      {/* Founders */}
      <section style={{ maxWidth: 760, margin: '0 auto', padding: '1.5rem 2rem 1rem' }}>
        <h2 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: 'clamp(1.6rem,3vw,2.2rem)', color: '#1A1A1A', textAlign: 'center', margin: '0 0 0.5rem' }}>
          Meet the founders
        </h2>
        <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.95rem', color: '#6B6B6B', textAlign: 'center', margin: '0 0 2.5rem' }}>
          The two building Okoiya, side by side.
        </p>
        <div className="about-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          {FOUNDERS.map((f, i) => <FounderCard key={i} f={f}/>)}
        </div>
      </section>

      {/* Values */}
      <section style={{ maxWidth: 1000, margin: '0 auto', padding: '3rem 2rem 1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '1.25rem' }}>
          {VALUES.map(({ icon: Icon, title, desc }, i) => (
            <div key={i} style={{ background: 'white', border: '1px solid #E5E0D8', borderRadius: '1.25rem', padding: '1.75rem 1.5rem' }}>
              <div style={{ width: 46, height: 46, borderRadius: '0.875rem', background: 'rgba(196,130,106,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                <Icon size={21} color="#C4826A"/>
              </div>
              <h3 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.2rem', color: '#1A1A1A', margin: '0 0 0.5rem' }}>{title}</h3>
              <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.9rem', color: '#6B6B6B', lineHeight: 1.7, margin: 0 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mission strip */}
      <section style={{ maxWidth: 760, margin: '0 auto', padding: '3rem 2rem' }}>
        <div style={{ background: 'white', border: '1px solid #E5E0D8', borderRadius: '1.5rem', padding: '2.5rem 2rem', textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: '1.5rem', color: '#1A1A1A', margin: '0 0 0.875rem' }}>What we believe</h2>
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '0.95rem', color: '#6B6B6B', lineHeight: 1.8, margin: 0 }}>
            Your wedding should feel like a celebration from the very first invite — not a second job. Every feature we build starts with one question: does this give the couple back time, calm, and joy?
          </p>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '1rem 2rem 5rem' }}>
        <div className="about-cta" style={{ background: 'linear-gradient(135deg,#C4826A 0%,#D4A882 50%,#9BAF9B 100%)', borderRadius: '2rem', maxWidth: 1000, margin: '0 auto', padding: '4rem 2rem', textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Gibs, serif', fontWeight: 400, fontSize: 'clamp(1.8rem,4vw,2.6rem)', color: 'white', margin: '0 0 1rem' }}>Plan yours with us</h2>
          <p style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '1rem', color: 'rgba(255,255,255,0.85)', margin: '0 0 2rem', lineHeight: 1.7 }}>
            Join the couples building their celebration on Okoiya.
          </p>
          <Link to="/signup">
            <button style={{ fontFamily: 'Nunito Sans, sans-serif', fontWeight: 700, fontSize: '1rem', color: '#1A1A1A', background: 'white', border: 'none', borderRadius: 100, padding: '1rem 2.5rem', cursor: 'pointer' }}>
              Start planning free →
            </button>
          </Link>
        </div>
      </section>

      <MarketingFooter/>

      <style>{`
        @media (max-width: 860px) {
          .about-story { grid-template-columns: 1fr !important; gap: 2rem !important; }
          .about-story-img { max-width: 420px; margin: 0 auto; }
        }
        @media (max-width: 640px) {
          .about-grid { grid-template-columns: 1fr !important; }
          .about-hero { padding: 3rem 1.25rem 2rem !important; }
        }
      `}</style>
    </div>
  )
}
